import { CorrelationEndpointSource } from '../../../../../@types/projects/types';
import { Sample } from '../../../../../@types/samples/types';

const isFiniteNumber = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export interface GasOilResolvedValues {
  swi: number | null;
  sgc: number | null;
  sor: number | null;
  /**
   * Residual oil used for the Corey saturation normalization. The
   * refined exponents/endpoint are fit on the critical residual Sorc
   * (the gas `sor` field, ~0.05); the lab parameters are fit on the
   * material-balance Sorg, so they normalize on `sor` itself.
   */
  normSor: number | null;
  no: number | null;
  ng: number | null;
  krgEndpoint: number | null;
  kroEndpoint: number;
}

export const resolveGasOilValues = (
  sample: Sample | null | undefined,
  source: CorrelationEndpointSource
): GasOilResolvedValues => {
  const inputValues = sample?.input_sample_values;
  const inputCurves = sample?.input_sample_curves?.gas_oil;
  const krelValues = sample?.k_rel_values?.gas_oil;
  const isRefinement = source === 'refinement';

  return {
    swi: inputValues?.swi ?? inputCurves?.sg_krel?.[0] ?? null,
    sgc: krelValues?.sgc ?? 0,
    sor: isRefinement
      ? krelValues?.true_sor_g ?? krelValues?.sor_g ?? null
      : krelValues?.sor_g ?? null,
    normSor: isRefinement
      ? krelValues?.sor ?? 0.05
      : krelValues?.sor_g ?? null,
    no: isRefinement
      ? krelValues?.true_no_g ?? krelValues?.no_g ?? null
      : krelValues?.no_g ?? null,
    ng: isRefinement
      ? krelValues?.true_ng ?? krelValues?.ng ?? null
      : krelValues?.ng ?? null,
    krgEndpoint: isRefinement
      ? krelValues?.true_krg ?? krelValues?.krg_exponent ?? inputValues?.krg_absolute ?? null
      : krelValues?.krg_exponent ?? inputValues?.krg_absolute ?? null,
    kroEndpoint: isRefinement ? krelValues?.true_kro_g ?? 1 : 1
  };
};

export const hasCompleteGasOilValues = (values: GasOilResolvedValues) =>
  isFiniteNumber(values.swi) &&
  isFiniteNumber(values.sgc) &&
  isFiniteNumber(values.sor) &&
  isFiniteNumber(values.no) &&
  isFiniteNumber(values.ng) &&
  isFiniteNumber(values.krgEndpoint) &&
  isFiniteNumber(values.kroEndpoint);
